import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

export class ThreeApp {
  /**
   * カメラ定義のための定数
   */
  static CAMERA_PARAM = {
    fovy: 26,
    aspect: window.innerWidth / window.innerHeight,
    near: 0.1,
    far: 50.0,
    position: new THREE.Vector3(0.0, 2.0, 5.0),
    lookAt: new THREE.Vector3(0.0, 0.0, 0.0),
  };
  /**
   * レンダラー定義のための定数
   */
  static RENDERER_PARAM = {
    clearColor: 0xffffff,
    width: window.innerWidth,
    height: window.innerHeight,
  };
  /**
   * 平行光源定義のための定数
   */
  static DIRECTIONAL_LIGHT_PARAM = {
    color: 0xffffff,
    intensity: 1.0,
    position: new THREE.Vector3(1.0, 1.0, 1.0),
  };
  /**
   * アンビエントライト定義のための定数
   */
  static AMBIENT_LIGHT_PARAM = {
    color: 0xffffff,
    intensity: 0.1,
  };
  /**
   * マテリアル定義のための定数
   */
  static MATERIAL_PARAM = {
    color: 0xffffff,
  };
  /**
   * レンダーターゲットの大きさ
   */
  static RENDER_TARGET_SIZE = 1024;

  canvas: HTMLCanvasElement; // canvas 要素
  canvasWidth: number;
  canvasHeight: number;
  renderer!: THREE.WebGLRenderer; // レンダラ
  scene!: THREE.Scene; // シーン
  camera!: THREE.PerspectiveCamera; // カメラ
  directionalLight!: THREE.DirectionalLight; // 平行光源（ディレクショナルライト）
  ambientLight!: THREE.AmbientLight; // 環境光（アンビエントライト）
  axesHelper!: THREE.AxesHelper; // 軸ヘルパー
  game: any; // 読み込んだ glTF 格納用

  offscreenScene!: THREE.Scene; // オフスクリーン用のシーン
  offscreenCamera!: THREE.OrthographicCamera; // オフスクリーン用のカメラ
  plane!: THREE.Mesh; // 板ポリゴン
  renderTarget!: THREE.WebGLRenderTarget; // オフスクリーン用のレンダーターゲット
  blackColor!: THREE.Color; // 背景色出し分けのため
  whiteColor!: THREE.Color; // 背景色出し分けのため

  currentScreenColor!: THREE.Color; // 現在のスクリーンカラー
  screenMaterial!: THREE.MeshBasicMaterial; // スクリーン用のマテリアル
  buttonColors: { [key: string]: THREE.Color }; // 各ボタンに対応する色

  raycaster: THREE.Raycaster;

  loadingOverlay!: HTMLElement;
  loadingProgress!: HTMLElement;

  buttonAFlag: boolean;
  buttonBFlag: boolean;
  buttonXFlag: boolean;
  buttonYFlag: boolean;
  buttonJyujiFlag: boolean;
  buttonA!: THREE.Mesh;
  buttonB!: THREE.Mesh;
  buttonX!: THREE.Mesh;
  buttonY!: THREE.Mesh;
  buttonJyuji!: THREE.Mesh;
  buttonAInitialX: number; // ボタンAの初期X位置
  buttonBInitialX: number; // ボタンBの初期X位置
  buttonXInitialX: number; // ボタンXの初期X位置
  buttonYInitialX: number; // ボタンYの初期X位置
  buttonJyujiInitialX: number; // ボタンJyujiの初期X位置
  buttonAAnimating: boolean; // ボタンAのアニメーション中フラグ
  buttonBAnimating: boolean; // ボタンBのアニメーション中フラグ
  buttonXAnimating: boolean; // ボタンXのアニメーション中フラグ
  buttonYAnimating: boolean; // ボタンYのアニメーション中フラグ
  buttonJyujiAnimating: boolean; // ボタンJyujiのアニメーション中フラグ
  buttonAPressedInSession: boolean;
  buttonBPressedInSession: boolean;
  buttonXPressedInSession: boolean;
  buttonYPressedInSession: boolean;
  buttonJyujiPressedInSession: boolean;
  allButtonsPressed: boolean;

  cursorVector: THREE.Vector3;
  groupVector!: THREE.Vector3;
  group!: THREE.Group;
  isDown: boolean;
  renderDebugLogged: boolean;

  /**
   * コンストラクタ
   * @constructor
   * @param {HTMLCanvasElement} canvas - 使用する canvas 要素
   */
  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    // CSSで指定されたサイズを取得（実際の表示サイズ）
    const computedStyle = window.getComputedStyle(this.canvas);
    this.canvasWidth = parseInt(computedStyle.width, 10);
    this.canvasHeight = parseInt(computedStyle.height, 10);

    this.render = this.render.bind(this);

    this.loadingOverlay = document.getElementById("loading-overlay")!;
    this.loadingProgress = document.getElementById("loading-progress")!;

    this.raycaster = new THREE.Raycaster();
    this.buttonAFlag = false;
    this.buttonBFlag = false;
    this.buttonXFlag = false;
    this.buttonYFlag = false;
    this.buttonJyujiFlag = false;
    this.buttonAAnimating = false;
    this.buttonBAnimating = false;
    this.buttonXAnimating = false;
    this.buttonYAnimating = false;
    this.buttonJyujiAnimating = false;
    this.buttonAPressedInSession = false;
    this.buttonBPressedInSession = false;
    this.buttonXPressedInSession = false;
    this.buttonYPressedInSession = false;
    this.buttonJyujiPressedInSession = false;
    this.allButtonsPressed = false;
    this.isDown = false;
    this.buttonAInitialX = 0;
    this.buttonBInitialX = 0;
    this.buttonXInitialX = 0;
    this.buttonYInitialX = 0;
    this.buttonJyujiInitialX = 0;
    this.cursorVector = new THREE.Vector3(0, 0, 0);
    this.renderDebugLogged = false;

    this.buttonColors = {
      A: new THREE.Color(0x00fa9a), // #00fa9a (ミディアムスプリンググリーン)
      B: new THREE.Color(0xff8c00), // #ff8c00 (ダークオレンジ)
      X: new THREE.Color(0xee82ee), // #ee82ee (バイオレット)
      Y: new THREE.Color(0x40e0d0), // #40e0d0 (ターコイズ)
      Jyuji: new THREE.Color(0xfa8072), // #fa8072 (サーモン)
    };

    this.currentScreenColor = new THREE.Color(0x000000);

    window.addEventListener("click", (mouseEvent) => {
      const rect = this.canvas.getBoundingClientRect();

      const x = ((mouseEvent.clientX - rect.left) / rect.width) * 2.0 - 1.0;
      const y = -((mouseEvent.clientY - rect.top) / rect.height) * 2.0 + 1.0;

      const v = new THREE.Vector2(x, y);
      this.raycaster.setFromCamera(v, this.camera);

      const intersectA = this.buttonA ? this.raycaster.intersectObject(this.buttonA, true) : [];
      const intersectB = this.buttonB ? this.raycaster.intersectObject(this.buttonB, true) : [];
      const intersectX = this.buttonX ? this.raycaster.intersectObject(this.buttonX, true) : [];
      const intersectY = this.buttonY ? this.raycaster.intersectObject(this.buttonY, true) : [];
      const intersectJyuji = this.buttonJyuji
        ? this.raycaster.intersectObject(this.buttonJyuji, true)
        : [];

      if (intersectA.length > 0 && !this.buttonAAnimating) {
        this.buttonAFlag = true;
        this.buttonAAnimating = true;
        this.buttonAPressedInSession = true;
      }
      if (intersectB.length > 0 && !this.buttonBAnimating) {
        this.buttonBFlag = true;
        this.buttonBAnimating = true;
        this.buttonBPressedInSession = true;
      }
      if (intersectX.length > 0 && !this.buttonXAnimating) {
        this.buttonXFlag = true;
        this.buttonXAnimating = true;
        this.buttonXPressedInSession = true;
      }
      if (intersectY.length > 0 && !this.buttonYAnimating) {
        this.buttonYFlag = true;
        this.buttonYAnimating = true;
        this.buttonYPressedInSession = true;
      }
      if (intersectJyuji.length > 0 && !this.buttonJyujiAnimating) {
        this.buttonJyujiFlag = true;
        this.buttonJyujiAnimating = true;
        this.buttonJyujiPressedInSession = true;
      }
    });

    window.addEventListener(
      "pointermove",
      (pointerEvent) => {
        const pointerX = pointerEvent.clientX;
        const pointerY = pointerEvent.clientY;
        const scaleX = (pointerX / window.innerWidth) * 2.0 - 1.0;
        const scaleY = (pointerY / window.innerHeight) * 2.0 - 1.0;

        this.cursorVector.set(scaleX, scaleY, 0);
      },
      false
    );

    let resizeTimeout: number;
    window.addEventListener(
      "resize",
      () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.updateSize();
        }, 100);
      },
      false
    );
  }

  /**
   * 角丸の横長矩形ジオメトリを作成
   * @param width 幅
   * @param height 高さ
   * @param radius 角丸の半径
   */
  createRoundedRectangleGeometry(width: number, height: number, radius: number) {
    const shape = new THREE.Shape();
    const x = -width / 2;
    const y = -height / 2;

    shape.moveTo(x + radius, y);
    shape.lineTo(x + width - radius, y);
    shape.quadraticCurveTo(x + width, y, x + width, y + radius);
    shape.lineTo(x + width, y + height - radius);
    shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    shape.lineTo(x + radius, y + height);
    shape.quadraticCurveTo(x, y + height, x, y + height - radius);
    shape.lineTo(x, y + radius);
    shape.quadraticCurveTo(x, y, x + radius, y);

    return new THREE.ShapeGeometry(shape);
  }

  /**
   * サイズを更新する処理
   */
  updateSize() {
    const computedStyle = window.getComputedStyle(this.canvas);
    this.canvasWidth = parseInt(computedStyle.width, 10);
    this.canvasHeight = parseInt(computedStyle.height, 10);

    this.renderer.setSize(this.canvasWidth, this.canvasHeight);
    this.camera.aspect = this.canvasWidth / this.canvasHeight;
    this.camera.updateProjectionMatrix();
  }

  /**
   * 初期化処理
   */
  init() {
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, alpha: true, antialias: true });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(this.canvasWidth, this.canvasHeight);

    setTimeout(() => {
      this.updateSize();
    }, 0);

    this.scene = new THREE.Scene();

    this.offscreenScene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(
      ThreeApp.CAMERA_PARAM.fovy,
      this.canvasWidth / this.canvasHeight,
      ThreeApp.CAMERA_PARAM.near,
      ThreeApp.CAMERA_PARAM.far
    );
    this.camera.position.copy(ThreeApp.CAMERA_PARAM.position);
    this.camera.lookAt(ThreeApp.CAMERA_PARAM.lookAt);

    this.directionalLight = new THREE.DirectionalLight(
      ThreeApp.DIRECTIONAL_LIGHT_PARAM.color,
      ThreeApp.DIRECTIONAL_LIGHT_PARAM.intensity
    );
    this.directionalLight.position.copy(ThreeApp.DIRECTIONAL_LIGHT_PARAM.position);
    this.offscreenScene.add(this.directionalLight);

    this.ambientLight = new THREE.AmbientLight(
      ThreeApp.AMBIENT_LIGHT_PARAM.color,
      ThreeApp.AMBIENT_LIGHT_PARAM.intensity
    );
    this.offscreenScene.add(this.ambientLight);

    const screenAspect = 1 / 1;
    const renderWidth = Math.floor(ThreeApp.RENDER_TARGET_SIZE * screenAspect);
    const renderHeight = ThreeApp.RENDER_TARGET_SIZE;

    this.renderTarget = new THREE.WebGLRenderTarget(renderWidth, renderHeight);

    const frustumHeight = 4; // 表示範囲の高さ
    const frustumWidth = frustumHeight * screenAspect; // アスペクト比に合わせた幅

    this.offscreenCamera = new THREE.OrthographicCamera(
      -frustumWidth / 2, // left
      frustumWidth / 2, // right
      frustumHeight / 2, // top
      -frustumHeight / 2, // bottom
      0.1, // near
      50 // far
    );

    this.offscreenCamera.position.set(0, 0, 5);
    this.offscreenCamera.lookAt(0, 0, 0);

    const planeGeometry = this.createRoundedRectangleGeometry(1.4, 0.8, 0.5);
    const planeMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
    this.plane = new THREE.Mesh(planeGeometry, planeMaterial);
    this.plane.position.set(-0.1, 0.64, 0.35);
    planeMaterial.map = this.renderTarget.texture;

    this.group = new THREE.Group();
    this.group.add(this.plane);

    if (this.game && this.game.scene) {
      this.group.add(this.game.scene);

      this.game.scene.traverse((obj: any) => {
        if (obj.name === "a" && obj instanceof THREE.Mesh) {
          this.buttonA = obj;
          this.buttonAInitialX = obj.position.x; // 初期位置を保存
        }
      });
      this.game.scene.traverse((obj: any) => {
        if (obj.name === "b" && obj instanceof THREE.Mesh) {
          this.buttonB = obj;
          this.buttonBInitialX = obj.position.x; // 初期位置を保存
        }
      });
      this.game.scene.traverse((obj: any) => {
        if (obj.name === "x" && obj instanceof THREE.Mesh) {
          this.buttonX = obj;
          this.buttonXInitialX = obj.position.x; // 初期位置を保存
        }
      });
      this.game.scene.traverse((obj: any) => {
        if (obj.name === "y" && obj instanceof THREE.Mesh) {
          this.buttonY = obj;
          this.buttonYInitialX = obj.position.x; // 初期位置を保存
        }
      });
      this.game.scene.traverse((obj: any) => {
        if (obj.name === "jyuji" && obj instanceof THREE.Mesh) {
          this.buttonJyuji = obj;
          this.buttonJyujiInitialX = obj.position.x; // 初期位置を保存
        }
      });

      this.game.scene.traverse((obj: any) => {
        if (obj.name === "ground") {
          obj.visible = false;
        }
      });

      this.game.scene.rotateY(-Math.PI / 2);
    } else {
      const geometry = new THREE.BoxGeometry(0.5, 0.5, 0.5);
      const material = new THREE.MeshLambertMaterial({ color: 0xff0000 });
      const gamePlaceholder = new THREE.Mesh(geometry, material);
      gamePlaceholder.position.set(1, 0, 0);
      this.group.add(gamePlaceholder);
    }

    this.groupVector = new THREE.Vector3(0, 0, -1);

    this.group.scale.set(0.7, 0.7, 0.7);

    this.scene.add(this.directionalLight.clone());
    this.scene.add(this.ambientLight.clone());

    this.blackColor = new THREE.Color(0x000000);
    this.whiteColor = new THREE.Color(0xffffff);

    this.screenMaterial = new THREE.MeshBasicMaterial({ color: this.currentScreenColor });

    this.scene.add(this.group);
  }

  /**
   * アセット（素材）のロードを行う Promise
   */
  load() {
    return new Promise<void>((resolve) => {
      this.showLoading();
      this.updateLoadingProgress(0);

      const canvasEl = document.getElementById("webgl-canvas") as HTMLCanvasElement | null;
      const assetsBase = (canvasEl?.dataset.assetsBase || "").replace(/\/$/, "");
      const gamePath = `${assetsBase}/images/pages/work-017/game.glb`;

      const loader = new GLTFLoader();
      loader.load(
        gamePath,
        (game) => {
          this.game = game;
          this.updateLoadingProgress(100);
          setTimeout(() => {
            this.hideLoading();
            resolve();
          }, 300);
        },
        (progress) => {
          if (progress.lengthComputable) {
            const percentComplete = (progress.loaded / progress.total) * 100;
            this.updateLoadingProgress(percentComplete);
          }
        },
        () => {
          this.hideLoading();
          resolve();
        }
      );
    });
  }

  /**
   * 描画処理
   */
  render() {
    requestAnimationFrame(this.render);

    if (!this.renderDebugLogged) {
      this.renderDebugLogged = true;
    }

    this.directionAnimation();

    this.buttonAnimation();

    this.renderer.setRenderTarget(this.renderTarget);
    this.renderer.setSize(this.renderTarget.width, this.renderTarget.height);
    this.renderer.setClearColor(this.currentScreenColor, 1);
    this.renderer.render(this.offscreenScene, this.offscreenCamera);

    this.renderer.setRenderTarget(null);
    this.renderer.setSize(this.canvasWidth, this.canvasHeight, true);
    this.renderer.setClearColor(0x000000, 0);

    this.renderer.render(this.scene, this.camera);
  }

  /**
   * 方向アニメーション
   */
  directionAnimation() {
    if (!this.group) {
      return;
    }

    const rotationStrength = 0.3; // 回転の強度を調整

    const targetRotationY = this.cursorVector.x * rotationStrength;

    const targetRotationX = -this.cursorVector.y * rotationStrength * 0.5;

    const lerpFactor = 0.05;
    this.group.rotation.y = THREE.MathUtils.lerp(
      this.group.rotation.y,
      targetRotationY,
      lerpFactor
    );
    this.group.rotation.x = THREE.MathUtils.lerp(
      this.group.rotation.x,
      targetRotationX,
      lerpFactor
    );
  }
  /**
   * ボタンアニメーション
   */
  buttonAnimation() {
    const speed = 0.01;
    const targetOffset = -0.04;

    if (this.buttonAFlag && this.buttonA) {
      if (this.buttonA.position.x > this.buttonAInitialX + targetOffset) {
        this.buttonA.position.x -= speed;
      } else {
        this.currentScreenColor.copy(this.buttonColors.A);
        this.buttonAFlag = false;
      }
    } else if (this.buttonAAnimating && this.buttonA) {
      if (this.buttonA.position.x < this.buttonAInitialX) {
        this.buttonA.position.x += speed;
      } else {
        this.buttonA.position.x = this.buttonAInitialX;
        this.buttonAAnimating = false;
      }
    }

    if (this.buttonBFlag && this.buttonB) {
      if (this.buttonB.position.x > this.buttonBInitialX + targetOffset) {
        this.buttonB.position.x -= speed;
      } else {
        this.currentScreenColor.copy(this.buttonColors.B);
        this.buttonBFlag = false;
      }
    } else if (this.buttonBAnimating && this.buttonB) {
      if (this.buttonB.position.x < this.buttonBInitialX) {
        this.buttonB.position.x += speed;
      } else {
        this.buttonB.position.x = this.buttonBInitialX;
        this.buttonBAnimating = false;
      }
    }

    if (this.buttonXFlag && this.buttonX) {
      if (this.buttonX.position.x > this.buttonXInitialX + targetOffset) {
        this.buttonX.position.x -= speed;
      } else {
        this.currentScreenColor.copy(this.buttonColors.X);
        this.buttonXFlag = false;
      }
    } else if (this.buttonXAnimating && this.buttonX) {
      if (this.buttonX.position.x < this.buttonXInitialX) {
        this.buttonX.position.x += speed;
      } else {
        this.buttonX.position.x = this.buttonXInitialX;
        this.buttonXAnimating = false;
      }
    }

    if (this.buttonYFlag && this.buttonY) {
      if (this.buttonY.position.x > this.buttonYInitialX + targetOffset) {
        this.buttonY.position.x -= speed;
      } else {
        this.currentScreenColor.copy(this.buttonColors.Y);
        this.buttonYFlag = false;
      }
    } else if (this.buttonYAnimating && this.buttonY) {
      if (this.buttonY.position.x < this.buttonYInitialX) {
        this.buttonY.position.x += speed;
      } else {
        this.buttonY.position.x = this.buttonYInitialX;
        this.buttonYAnimating = false;
      }
    }

    if (this.buttonJyujiFlag && this.buttonJyuji) {
      if (this.buttonJyuji.position.x > this.buttonJyujiInitialX + targetOffset) {
        this.buttonJyuji.position.x -= speed;
      } else {
        this.currentScreenColor.copy(this.buttonColors.Jyuji);
        this.buttonJyujiFlag = false;
      }
    } else if (this.buttonJyujiAnimating && this.buttonJyuji) {
      if (this.buttonJyuji.position.x < this.buttonJyujiInitialX) {
        this.buttonJyuji.position.x += speed;
      } else {
        this.buttonJyuji.position.x = this.buttonJyujiInitialX;
        this.buttonJyujiAnimating = false;
      }
    }
  }

  /**
   * ローディング画面を表示
   */
  showLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.style.display = "flex";
    }
  }

  /**
   * ローディング画面を非表示
   */
  hideLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.style.display = "none";
    }
  }

  /**
   * ローディング進捗を更新
   * @param {number} progress - 進捗率（0-100）
   */
  updateLoadingProgress(progress: number) {
    if (this.loadingProgress) {
      this.loadingProgress.textContent = `${Math.round(progress)}%`;
    }
  }
}
